<template>
  <Sheet v-model:open="open" :inline="inline" :aria-label="text.biddingAs" @after-close="emit('close')">
    <div class="bid-sheet">
      <!-- ── The asset ──────────────────────────────────────────────────── -->
      <div class="bid-sheet__asset">
        <div class="bid-sheet__thumbnail">
          <AssetPhoto :src="imageUrl" :alt="title" size="medium" />
        </div>
        <div class="bid-sheet__identity">
          <div class="bid-sheet__title">{{ title }}</div>
          <div class="bid-sheet__run">
            <span>{{ runLabel }}</span>
            <span v-if="clockLabel" class="bid-sheet__clock" :class="{ 'bid-sheet__clock--urgent': urgent }">{{ clockLabel }}</span>
            <span v-if="headline" class="bid-sheet__headline">{{ headline.text }}</span>
          </div>
          <!-- All three marks or none of them: the row collapses to zero height
               together, so an unmarked asset does not leave a gap where a badge
               would have been. -->
          <div v-if="marked" class="bid-sheet__marks">
            <span v-if="drawnLights.length" class="bid-sheet__auction-lights">
              <span
                v-for="(light, index) in drawnLights"
                :key="`${light.code}-${index}`"
                class="bid-sheet__auction-light"
                :class="`bid-sheet__auction-light--${light.name}`"
              >{{ light.code }}</span>
              <span class="bid-sheet__visually-hidden">{{ lightsLabel }}</span>
            </span>
            <span v-if="hasNotes" class="bid-sheet__mark">{{ text.notes }}</span>
            <span v-if="liked" class="bid-sheet__mark bid-sheet__mark--liked">{{ text.liked }}</span>
          </div>
        </div>
      </div>

      <!-- ── The money ──────────────────────────────────────────────────── -->
      <div class="bid-sheet__money" :class="`bid-sheet__money--${severity}`">
        <span v-if="price === null" class="bid-sheet__price bid-sheet__price--unset">{{ text.priceNotSet }}</span>
        <template v-else>
          <span class="bid-sheet__price">{{ money(price) }}</span>
          <!-- `final` drops the word rather than swapping it: a settled asset
               reading "$27,400 current" is the one mistake a fixed word makes. -->
          <span v-if="!final" class="bid-sheet__money-label">{{ text.moneyLabel }}</span>
        </template>
        <span v-if="targetPrice !== null && !final" class="bid-sheet__target">
          <i class="pi pi-flag" aria-hidden="true" /><span class="bid-sheet__visually-hidden">{{ targetLabel }}</span>
          <span aria-hidden="true">{{ money(targetPrice) }}</span>
        </span>
      </div>

      <!-- ── The receipt ────────────────────────────────────────────────── -->
      <!-- A settled asset has no tabs, no steppers and no dealership row: there
           is nothing left to bid as. `disabled` carries the sentence, which is
           why there is no `outcome` prop for it to duplicate. -->
      <p v-if="final" class="bid-sheet__receipt" :class="`bid-sheet__receipt--${severity}`">
        <i v-if="lockedIcon" :class="`pi pi-${lockedIcon}`" aria-hidden="true" />{{ lockedText }}
      </p>

      <template v-else>
        <!-- ── The two tabs ─────────────────────────────────────────────── -->
        <div class="bid-sheet__tabs" role="tablist" :aria-label="text.biddingAs">
          <button
            :id="`${sheetId}-tab-live`"
            type="button"
            role="tab"
            class="bid-sheet__tab"
            :class="{ 'bid-sheet__tab--selected': mode === 'live' }"
            :aria-selected="mode === 'live'"
            :aria-controls="`${sheetId}-panel`"
            :aria-describedby="proxyLocked ? `${sheetId}-lock` : undefined"
            :disabled="proxyLocked"
            :tabindex="mode === 'live' ? 0 : -1"
            @click="chooseMode('live')"
          >
            {{ text.liveBid }}
            <i v-if="proxyLocked" class="pi pi-lock bid-sheet__tab-lock" aria-hidden="true" />
          </button>
          <button
            :id="`${sheetId}-tab-proxy`"
            type="button"
            role="tab"
            class="bid-sheet__tab"
            :class="{ 'bid-sheet__tab--selected': mode === 'proxy' }"
            :aria-selected="mode === 'proxy'"
            :aria-controls="`${sheetId}-panel`"
            :tabindex="mode === 'proxy' ? 0 : -1"
            @click="chooseMode('proxy')"
          >{{ text.proxy }}</button>
        </div>
        <!-- The words the notice used to render. They cost 85px in one panel
             and nothing in the other, which is exactly the asymmetry the height
             parity rule forbids — so they stay reachable and stop taking room. -->
        <span v-if="proxyLocked" :id="`${sheetId}-lock`" class="bid-sheet__visually-hidden">{{ proxyLockLabel }}</span>

        <!-- ── The panel ────────────────────────────────────────────────── -->
        <!-- Increment, chips and control keep their own heights in every state,
             both modes, field open or closed. The dealer row is extra and
             optional. A dealer switching tabs mid-sale is deciding how to
             commit money in a lane that is still running, and a tray that grows
             under the thumb moves the control they were reaching for. -->
        <div
          :id="`${sheetId}-panel`"
          class="bid-sheet__panel"
          role="tabpanel"
          :aria-labelledby="`${sheetId}-tab-${mode}`"
        >
          <!-- The increment ladder, in both panels and in the same slot. -->
          <div class="bid-sheet__increment-label">
            <span>{{ text.increment }}</span>
            <button
              v-if="mode === 'proxy' && !typing"
              type="button"
              class="bid-sheet__type-toggle"
              @click="openField"
            >{{ text.typeAmount }}</button>
            <button
              v-else-if="mode === 'proxy'"
              type="button"
              class="bid-sheet__type-toggle"
              @click="closeField"
            >{{ text.closeKeypad }}</button>
            <!-- One element in one slot, whichever it says: a notice that
                 arrived as a second line would move the control the dealer is
                 reaching for, which is the rule the whole panel is built on. -->
            <span
              v-else
              class="bid-sheet__increment-hint"
              :class="{ 'bid-sheet__increment-hint--changed': incrementNotice !== null }"
            >{{ incrementNotice ?? text.tapToChange }}</span>
          </div>

          <!-- The chips, or — in the proxy panel while typing — the field that
               takes their slot. Same 48px either way, which is what makes the
               OS keyboard affordable where a drawn keypad was not. -->
          <div v-if="!typing" class="bid-sheet__chips">
            <!-- No ladder: one cell reading a dash, in the chips' own slot and
                 at their own height, rather than four multiples of a figure
                 nobody set. Not a control — there is nothing to choose. -->
            <span v-if="increments === null" class="bid-sheet__chip bid-sheet__chip--unset">—</span>
            <button
              v-for="chip in increments ?? []"
              :key="chip.multiplier"
              type="button"
              class="bid-sheet__chip"
              :class="{
                'bid-sheet__chip--selected': chip.multiplier === selectedMultiplier,
                'bid-sheet__chip--changed': incrementNotice !== null,
              }"
              :aria-pressed="chip.multiplier === selectedMultiplier"
              @click="chooseMultiplier(chip.multiplier)"
            >+{{ chipLabel(chip.amount) }}</button>
          </div>
          <div v-else class="bid-sheet__field">
            <input
              ref="fieldRef"
              class="bid-sheet__input"
              :class="{ 'bid-sheet__input--below': belowFloor }"
              type="text"
              inputmode="numeric"
              :aria-label="text.setProxy"
              :aria-describedby="belowFloor ? `${sheetId}-below` : undefined"
              :value="fieldText"
              @input="onFieldInput"
            >
            <span v-if="belowFloor" :id="`${sheetId}-below`" class="bid-sheet__below">{{ belowFloorLabel }}</span>
          </div>

          <!-- ── Bidding as ─────────────────────────────────────────────── -->
          <!-- A row, not a page. A sheet over a sheet is two dismiss gestures
               deep and a dealer loses which one they are in; the whole roster is
               visible at once, so choosing is one tap.

               ABSENT rather than empty when the roster is: the identities are a
               server record, so an empty list is a real state — the runtime token
               has not resolved yet, or nobody has issued this dealer a badge —
               and a labelled radiogroup with no radios in it announces a choice
               that is not being offered. -->
          <DealershipPicker
            v-if="biddingAsDealerships.length"
            v-model="biddingAsId"
            layout="strip"
            class="bid-sheet__dealerships"
            :items="dealershipPickerItems"
            :label="text.biddingAs"
          />

          <!-- LIVE: the slide, or the words that replace it. -->
          <div v-if="mode === 'live'" class="bid-sheet__control">
            <template v-if="liveControlAvailable">
              <button
                type="button"
                class="bid-sheet__bid"
                :class="{ 'bid-sheet__bid--hidden': armed }"
                @click="armed = true"
              >
                <span>{{ text.placeBid }}</span><span>{{ money(liveAmount) }}</span>
              </button>
              <!-- `reset-key` is the live amount, so an inbound bid that
                   re-prices the slide also ends a slide already in flight: the
                   dealer gets the new number and a fresh gesture to confirm it
                   with, rather than their old travel completing against it. -->
              <SlideToConfirm
                :label="slideLabel"
                :armed="armed"
                :reset-key="liveAmount"
                @confirm="placeBid"
              />
            </template>
            <!-- No greyed control anywhere in this tray: the words REPLACE the
                 slide rather than sitting on top of a dead one. -->
            <p v-else class="bid-sheet__unavailable">
              <i v-if="unavailableIcon" :class="`pi pi-${unavailableIcon}`" aria-hidden="true" />
              <span>{{ unavailableText }}</span>
              <span v-if="showStandingOrderHint" class="bid-sheet__hint">{{ text.standingOrderHint }}</span>
            </p>
          </div>

          <!-- PROXY: two steppers and the primary between them. -->
          <div v-else class="bid-sheet__control bid-sheet__steppers">
            <button
              type="button"
              class="bid-sheet__step"
              :disabled="typing || atFloor || !stepsAvailable"
              :aria-label="stepLabel('−')"
              @click="stepDraft(-1)"
            >−</button>
            <button
              type="button"
              class="bid-sheet__primary"
              :class="`bid-sheet__primary--${primaryTone}`"
              :aria-describedby="primaryReading === 'cancel' ? `${sheetId}-caveat` : undefined"
              @click="pressPrimary"
            >
              <span>{{ primaryLabel }}</span>
              <span v-if="primaryAmount !== null">{{ money(primaryAmount) }}</span>
            </button>
            <button
              type="button"
              class="bid-sheet__step"
              :disabled="typing || !stepsAvailable"
              :aria-label="stepLabel('+')"
              @click="stepDraft(1)"
            >+</button>
          </div>
          <span :id="`${sheetId}-caveat`" class="bid-sheet__visually-hidden">{{ text.cancelProxyCaveat }}</span>
        </div>
      </template>
    </div>
  </Sheet>
</template>

<script setup>
// The bid tray: the surface where a dealer commits money on one asset.
//
// It is the second half of the interaction BidCard opens, and twenty of its
// props are BidCard's under BidCard's names, types and polarity — so the two
// read the same asset the same way, and a fact they both carry cannot drift
// into being rendered two ways.
//
// It DRAWS WHAT IT IS TOLD. Every `computed` in the version this replaces was a
// store read; none survive. What is left is arithmetic the tray genuinely owns
// — the countdown, the money format, the live amount and the proxy floor — and
// the last two are exported from ../../utils/bidSheetArithmetic.js, because the
// floor is derived four times across this component and the server and the
// failure when two of those drift is silent.
//
// TWO RULES SHAPE THE WHOLE LAYOUT.
//
// One: THE PANEL IS ONE HEIGHT, in both modes and in every state, including
// while the numeric field is open. A dealer switching tabs mid-sale is deciding
// how to commit money in a lane that is still running, and a tray that grows
// under the thumb moves the control they were reaching for. This is why the
// proxy-lock notice is an `aria-describedby` sentence rather than a paragraph,
// why the cancel caveat is another, and why the numeric field takes the chips'
// slot instead of adding a keypad below them.
//
// Two: THERE IS NO GREYED CONTROL. Where the card keeps a dead bid button in
// its row, the tray replaces the control with the words for why it is away —
// the tray's proxy is in a different panel, so nothing moves when it does.
//
// The prose above is for whoever EDITS this file. What a consumer reads is the
// Storybook page, whose text comes from `docs.description.component` in
// BidSheet.stories.js.
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import DealershipPicker from '../DealershipPicker/DealershipPicker.vue';
import AssetPhoto from '../AssetPhoto/AssetPhoto.vue';
import Sheet from '../Sheet/Sheet.vue';
import SlideToConfirm from '../SlideToConfirm/SlideToConfirm.vue';
import { auctionLightName } from '../../utils/formatters';
import { bidAmount, proxyFloor } from '../../utils/bidSheetArithmetic';
import {
  BID_INCREMENT_MULTIPLIERS,
  STANDARD_BID_INCREMENTS,
  isAbsentOrNonBlankString,
  isAbsentOrWholeDollars,
  isNonBlankString,
  isValidDealershipRoster,
  isValidDisabled,
  isValidHeadline,
  isValidIncrements,
} from '../../utils/bidSheetShapes';
import {
  formatMoneyDefault,
  interpolate,
  isValidBidSheetI18n,
  mergeTranslations,
} from '../../utils/bidSheetTranslations';

defineOptions({ name: 'BidSheet' });

const props = defineProps({
  // ── The asset ────────────────────────────────────────────────────────────
  // The item's own `_id` — the identity on both planes, and what every emit
  // carries so a page does not have to work out which tray spoke when the asset
  // changes underneath an open sheet.
  assetId: { type: String, required: true, validator: isNonBlankString },
  // Already assembled — "2019 Ram 1500". The page owns the phrasing; a lane
  // sells whatever the sale consigns, and a year and a model say nothing about
  // a pallet of parts. The version this replaces joined year/make/model itself.
  title: { type: String, required: true, validator: isNonBlankString },
  // A label, not a quantity: the runtime formats it padded (`0142`), which a
  // Number would discard. It repeats across sales, so it is never the identity.
  runNumber: { type: String, required: true, validator: isNonBlankString },
  lane: { type: String, default: '' },
  /**
   * The auction house's condition codes, in the order they should read. A code
   * may legitimately repeat, so the render key is `code-index`.
   * @type {('R'|'G'|'Y'|'B'|'W')[]}
   */
  // Validated by RESOLUTION rather than by membership: `AUCTION_LIGHT_NAMES` is
  // keyed lower case and the feed sends upper, so a literal `includes` rejects
  // every real value. Unresolved codes are DROPPED rather than warned about —
  // the app synthesizes a first-letter code for anything it does not recognize,
  // so unknown codes reach this tray by design and are not caller error.
  lights: {
    type: Array,
    default: () => [],
    validator: (value) => Array.isArray(value),
  },
  // `hasNotes` and not `notes`: a prop named for the content invites the
  // content, an array is truthy, and the badge would render on a tray handed
  // the wrong thing. BidCard's prop, name and reasoning verbatim.
  hasNotes: { type: Boolean, default: false },
  // The dealer's own intent to buy — the one mark that says nothing about the
  // asset. BidCard's NAME, deliberately: the card and the tray behind it read
  // the same word for the same fact.
  liked: { type: Boolean, default: false },
  // An instant, in milliseconds. The floor is above every plausible seconds
  // value so that seconds-passed-as-milliseconds is loud rather than rendering
  // as 1970. BidCard's prop, type and validator.
  endsAt: {
    type: Number,
    default: null,
    validator: (value) => value === null || (Number.isFinite(value) && value > 1e12),
  },
  // Stops the timer, not the arithmetic — what makes a story hold still.
  countdown: { type: Boolean, default: true },
  imageUrl: { type: String, default: null, validator: isAbsentOrNonBlankString },
  /**
   * The sale-state badge after the run and lane. `icon` is accepted so the
   * shape stays BidCard's, and is not drawn — a white pip stands in, so the
   * badge stays the height of an auction light.
   * @type {{ text: string, icon?: string }}
   */
  // BidCard's polymorphic slot, and NOT a `live` Boolean: a Boolean locks the
  // tray to one word, and the same slot carries LIVE, a sale type or a standing.
  headline: { type: Object, default: null, validator: isValidHeadline },

  // ── The money ────────────────────────────────────────────────────────────
  // The live current bid, and the only price the tray receives. `null` is an
  // asset that has not opened: the live control does not render then regardless
  // of `canBid`, because the tray is refusing arithmetic it cannot perform
  // rather than judging a business rule, and the alternative is `NaN` on the
  // one control that commits money.
  price: { type: Number, default: null, validator: isAbsentOrWholeDollars },
  // The dealer's own ceiling from the planning worksheet — NOT an armed proxy.
  targetPrice: { type: Number, default: null, validator: isAbsentOrWholeDollars },
  /**
   * The increment ladder, the store's own shape.
   * @type {{ multiplier: number, amount: number }[]}
   */
  // A REAL default rather than an empty one. See STANDARD_BID_INCREMENTS: the
  // magic `|| 100` this replaces was right about the number and wrong about
  // where it lived, and an empty default would render `$NaN` through a legal
  // prop value.
  //
  // `null` IS A LADDER'S ABSENCE, and a separate answer from omitting the prop.
  // The engine gives an asset its increment when the asset starts, so one that
  // has not started has none — and every figure built on it is then a figure
  // nobody set. The tray draws a dash in the chips' slot, takes the live
  // control away and leaves the typed ceiling, which is the one thing still
  // worth setting on an asset that has not opened. An EMPTY array is still
  // refused: that is a ladder with nothing in it, which is the `$NaN` the
  // validator exists for.
  increments: {
    type: Array,
    default: () => STANDARD_BID_INCREMENTS,
    validator: (value) => value === null || isValidIncrements(value),
  },
  // Where the draft OPENS. The app computes it — at market, so the suggestion
  // clears the proxy bids already in — and the tray is handed the answer rather
  // than the reasoning, which is what lets that reasoning change without
  // touching this contract. Named for its status rather than its position:
  // every name built on *start* reads as a floor, which it is not.
  suggestedProxy: { type: Number, required: true, validator: Number.isInteger },
  // The dealer's armed ceiling on the server, raw. It is what turns the primary
  // into a cancel, and it must survive being spent — reading the ceiling you
  // set is how you raise it.
  proxyAmount: { type: Number, default: null, validator: isAbsentOrWholeDollars },
  // The armed proxy can no longer fire. BidCard's name and POLARITY. It cannot
  // be computed here: the engine's test is whether a proxy can reach the next
  // valid bid, and an unspent proxy is what takes the live tab away.
  proxySpent: { type: Boolean, default: false },

  // ── The sale's state ─────────────────────────────────────────────────────
  // Whether a live bid is on offer — whether the control renders, not whether
  // it reports. BidCard's asymmetry: false takes the live control and LEAVES
  // the proxy, because a standing order that fires on resume is the entire
  // point of a paused tray.
  canBid: { type: Boolean, default: false },
  // The asset has run and the sale is complete. This is what separates a pause
  // from an ending, and it is why the standing-order hint cannot appear on a
  // sale that will never resume. It takes the proxy away too.
  final: { type: Boolean, default: false },
  // PrimeVue's vocabulary and BidCard's prop, unchanged. `success` stays
  // deliberately unusable: mint is the bid control's color and would compete
  // with the only thing here that costs money.
  severity: {
    type: String,
    default: 'secondary',
    validator: (value) => ['warn', 'danger', 'secondary'].includes(value),
  },
  // The dealer already holds the high bid. Like `proxySpent` this cannot be
  // computed here — it is a comparison against the badge the runtime reports
  // and the dealerships this session may bid under, and the tray sees neither.
  //
  // It is the one absent-control state that needs no caller words: the tray is
  // told the fact directly, so it renders its own sentence rather than
  // depending on a caller to phrase it.
  winning: { type: Boolean, default: false },
  /**
   * Why a control is away — and, when `final`, why every control is.
   * @type {{ text?: string, icon?: string }}
   */
  // `null` is the only way to say "not disabled", which is why it is an object.
  // One slot for two scales, because `final` already tells the tray which it is
  // being asked — and on a settled tray it is also the receipt's sentence,
  // which is why there is no `outcome` prop duplicating it.
  disabled: { type: Object, default: null, validator: isValidDisabled },
  // The sale has just changed this asset's increment, in the caller's words.
  //
  // THE TRAY KEEPS NO TIMER FOR IT. A notice is current or it is not, and only
  // the app knows when the change landed — so the app passes the sentence while
  // it is current and `null` after, and the tray draws what it is told. That is
  // also why the words are the caller's, as `disabled.text` is: this is one
  // sentence about a runtime event, not a state the tray can name for itself.
  //
  // While it is set the notice takes the hint's slot and the ladder is marked,
  // so the figures that moved are visibly the ones that moved. Neither changes
  // the panel's height.
  incrementNotice: { type: String, default: null, validator: isAbsentOrNonBlankString },

  // ── The rest ─────────────────────────────────────────────────────────────
  // Read once at mount — the prefix is the contract, and the tray moves the tab
  // itself afterwards. The default is proxy because that is the mode available
  // in strictly more situations. An unspent proxy opens on proxy regardless.
  initialMode: {
    type: String,
    default: 'proxy',
    validator: (value) => ['live', 'proxy'].includes(value),
  },
  /**
   * The dealerships this dealer may bid as, in the order they should read.
   * @type {{ id: string, name: string, role: string | null }[]}
   */
  // REQUIRED, and an empty roster IS a state: the identities are a server
  // record fetched with the runtime token, so the list is empty until that
  // resolves and empty for a dealer nobody has issued a badge to. The tray
  // answers by leaving the chip row out. Still required rather than
  // `default: () => []` — "no dealerships" and "nobody passed the roster" are
  // different, and a default erases the difference.
  biddingAsDealerships: { type: Array, required: true, validator: isValidDealershipRoster },
  /**
   * One language's finished text, and the money policy. A partial
   * `translations` merges over the English defaults key by key. The key list is
   * `BID_SHEET_TRANSLATIONS` in ../../utils/bidSheetTranslations.js.
   * @type {{ translations?: object, formatMoney?: (amount: number) => string }}
   */
  i18n: { type: Object, default: null, validator: isValidBidSheetI18n },
  /**
   * Sheet's own `inline`, forwarded: the tray in the flow of whatever mounted
   * it instead of as a modal over the page. For surfaces that document the
   * tray — the catalog renders every story this way — never for the app.
   */
  inline: { type: Boolean, default: false },
});

// The dealership a bid commits under. A MODEL rather than a prop for the same
// reason the multiplier is one: the app owns the session fact and the tray
// proposes a change. There is no `biddingAs` name beside it — the tray is
// handed the whole roster in order to draw the chip row, so the current name is
// already in the component and passing it again would be one fact twice.
// `null` when the roster is empty, which is the one case where there is no
// dealership to be bidding as. Not `required`, for the same reason the roster
// is allowed to be empty: a tray opened before the runtime token resolves has
// nobody to name yet, and a required non-blank string would make the app invent
// one to satisfy the contract.
const biddingAsId = defineModel('biddingAsId', {
  type: String,
  default: null,
  validator: isAbsentOrNonBlankString,
});

// The chosen multiple of the asset's increment. A MODEL because the version
// this replaces wrote `store.bidIncrementMultiplier` straight from a click
// handler — a component in a library mutating a preference shared by every tray
// and every lane.
const selectedMultiplier = defineModel('selectedMultiplier', {
  type: Number,
  default: 1,
  validator: (value) => BID_INCREMENT_MULTIPLIERS.includes(value),
});

const emit = defineEmits([
  // Verb-object, all three, landing one-to-one on the store methods the call
  // sites hand them to. `place-bid` is deliberately NOT the card's `bid`: a
  // page binds `@bid` on a card to OPEN this tray and `@place-bid` on the tray
  // to COMMIT the money, and one word on both acts in one template is how they
  // get confused.
  'place-bid',
  'set-proxy',
  'clear-proxy',
  'close',
]);

// Stable across server and client, and unique per instance — which is what the
// prototype's `Math.random()` was reaching for. Two trays open for one run
// resolved to the same `aria-describedby` id, and a screen reader on the second
// announced the first one's message.
const sheetId = useId();

// ── Language ───────────────────────────────────────────────────────────────
const text = computed(() => mergeTranslations(props.i18n?.translations));
const money = (amount) => (props.i18n?.formatMoney ?? formatMoneyDefault)(amount);

// ── Visibility ─────────────────────────────────────────────────────────────
// Mounts open; any dismissal sets this false, and Sheet's `after-close` — once
// the slide-out has finished — tells the parent to unmount us. Emitting before
// the transition completes cuts the animation, because the parent unmounts on it.
const open = ref(true);

// ── The asset header ───────────────────────────────────────────────────────
const runLabel = computed(() =>
  props.lane
    ? interpolate(text.value.runLane, { run: props.runNumber, lane: props.lane })
    : interpolate(text.value.run, { run: props.runNumber })
);

// Resolved rather than looked up, and unresolved codes are dropped: the app
// synthesizes a first-letter code for anything it does not recognize, so an
// unknown code is data rather than caller error.
const drawnLights = computed(() =>
  props.lights
    .map((code) => ({ code, name: auctionLightName(code) }))
    .filter((light) => light.name !== null)
);

const marked = computed(() => drawnLights.value.length > 0 || props.hasNotes || props.liked);

const lightsLabel = computed(() =>
  interpolate(text.value.lights, {
    lights: drawnLights.value
      .map((light) => text.value.lightNames[light.code.toUpperCase()] ?? light.code)
      .join(', '),
  })
);

// ── The clock ──────────────────────────────────────────────────────────────
// Derived from `endsAt` on every tick rather than by decrementing a counter, so
// a backgrounded timer catches up instead of drifting and an anti-snipe
// extension re-bases by itself. BidCard's rule, and its three phrasings.
const now = ref(Date.now());
let ticker = null;

if (props.countdown) {
  ticker = setInterval(() => { now.value = Date.now(); }, 1000);
}

const pad = (value) => String(value).padStart(2, '0');

const secondsRemaining = computed(() =>
  props.endsAt === null ? null : Math.max(0, Math.round((props.endsAt - now.value) / 1000))
);

const clockLabel = computed(() => {
  const seconds = secondsRemaining.value;
  if (seconds === null) return null;
  if (seconds < 60) return interpolate(text.value.secondsLeft, { seconds });
  if (seconds < 3600) {
    return interpolate(text.value.minutesLeft, {
      minutes: Math.floor(seconds / 60),
      seconds: pad(seconds % 60),
    });
  }
  return interpolate(text.value.closesIn, {
    hours: Math.floor(seconds / 3600),
    minutes: pad(Math.floor((seconds % 3600) / 60)),
  });
});

// One threshold, and weight carries it as much as color — weight survives
// grayscale, color blindness and a phone in sunlight. BidCard's rule.
const urgent = computed(() => secondsRemaining.value !== null && secondsRemaining.value <= 10);

const targetLabel = computed(() =>
  interpolate(text.value.target, { amount: props.targetPrice === null ? '' : money(props.targetPrice) })
);

// ── The tabs ───────────────────────────────────────────────────────────────
// An armed proxy IS the bid on this asset: bidding live on top of it only
// raises the price the dealer's own ceiling is measured against. A SPENT proxy
// does not lock — the engine will never act on it again, and holding the live
// tab shut would trap the dealer behind a ceiling that does nothing.
const proxyLocked = computed(() => props.proxyAmount !== null && !props.proxySpent);

const proxyLockLabel = computed(() =>
  interpolate(text.value.proxyLockNotice, { price: money(props.proxyAmount ?? 0) })
);

const mode = ref(proxyLocked.value ? 'proxy' : props.initialMode);

// A proxy armed elsewhere — another device, the video dock — while the tray
// sits open must move it off the live tab rather than leave a slide the engine
// would refuse anyway.
watch(proxyLocked, (locked) => { if (locked) chooseMode('proxy'); });

function chooseMode(next) {
  if (mode.value === next) return;
  mode.value = next;
}

// ── The increment ladder ───────────────────────────────────────────────────
// The `multiplier: 1` entry is this asset's real increment, and every figure in
// the tray is built on it. The validator guarantees the entry exists.
// `null` for an asset with no ladder at all — the validator guarantees the
// entry exists whenever there IS one.
const baseIncrement = computed(() =>
  props.increments === null ? null : props.increments.find((entry) => entry.multiplier === 1).amount
);

const chipLabel = (amount) => (amount >= 1000 ? `${amount / 1000}k` : `${amount}`);

// What a reader hears on a stepper. With no increment there is no amount to
// name, so it is the sign alone rather than a figure nobody set.
const stepLabel = (sign) => (baseIncrement.value === null ? sign : `${sign}${chipLabel(baseIncrement.value)}`);

// Whether the ladder exists. The steppers move the draft BY the increment, so
// with none there is nothing for them to move it by.
const stepsAvailable = computed(() => baseIncrement.value !== null);

function chooseMultiplier(multiplier) {
  if (multiplier === selectedMultiplier.value) return;
  selectedMultiplier.value = multiplier;
}

// ── The live control ───────────────────────────────────────────────────────
const liveAmount = computed(() =>
  bidAmount({ price: props.price, increment: baseIncrement.value, multiplier: selectedMultiplier.value })
);

// The slide track owns no formatting of its own — it is a generic control that
// renders a finished label — so the tray composes the "Slide to place bid · $X"
// string here, off the same money policy every other figure in the tray uses.
// The separators are non-breaking so the amount never wraps away from the words.
// The words come from the catalog: they used to be written in here, so the one
// control that spends the money was the one control that stayed in English.
const slideLabel = computed(() => `${text.value.slidePlaceBid}\u00a0·\u00a0${money(liveAmount.value)}`);

// Absent, never disabled. `price === null` is in here for a different reason
// from the rest: the tray cannot compute an amount without a current bid, and
// that is a refusal to do arithmetic rather than a judgement about the sale.
// `liveAmount === null` covers both refusals in one test: no price to bid from,
// and no increment to bid BY. Either way the tray cannot name the amount the
// slide would commit, and the control is absent rather than priced at `NaN`.
const liveControlAvailable = computed(
  () => props.canBid && !props.winning && props.disabled === null && liveAmount.value !== null && !proxyLocked.value
);

const armed = ref(true); // the tray opens armed — the slide is shown directly

// Leading is the exception: the tray is told the fact, so it says so in its own
// words. Everything else — a pause, a credit hold, an asset that has not opened
// — is `canBid: false` with `disabled` text the caller chose, because the tray
// is never told WHY.
const unavailableText = computed(() => {
  if (props.winning) return text.value.winningBid;
  return props.disabled?.text || text.value.bidUnavailable;
});

const unavailableIcon = computed(() => {
  if (props.winning) return 'trophy';
  return props.disabled?.icon || 'lock';
});

// Shown whenever the live control is away and the sale can still resume — keyed
// to that rather than to a pause, since the tray cannot see a pause. `final`
// takes it away, because a standing order cannot fire on a sale that has ended.
const showStandingOrderHint = computed(() => !props.final && !proxyLocked.value);

// ── The proxy draft ────────────────────────────────────────────────────────
// The tray owns the DRAFT — a stepper's value is view state — and never the
// seed, which it could not compute anyway: that arithmetic needs a market value
// this contract deliberately does not carry.
const draft = ref(props.suggestedProxy);

// Whether the DEALER moved it, tracked rather than inferred from
// `draft === proxyAmount`. The two look identical until the proxy is spent, and
// then they disagree in the one direction that matters: a spent proxy's seed is
// re-floored to `price + increment` — the ceiling it would otherwise restore has
// already lost — so its draft never equals its amount, and equality would read
// a resting tray as stepped and put UPDATE where CANCEL belongs. A spent proxy
// is still a row on the server and has to stay cancelable.
const draftTouched = ref(false);

// The numeric field's own state, declared with the draft it edits rather than
// beside the functions that drive it: the primary reads `typing` to decide what
// it says and what it does, and that is three sections above where the field is
// opened.
const typing = ref(false);
const fieldRef = ref(null);
const fieldText = ref('');

// The floor is where the engine starts acting. `null` on an unopened asset is a
// real answer: with no current bid there is nothing a ceiling can sit above.
const floor = computed(() => proxyFloor({ price: props.price, baseIncrement: baseIncrement.value }));

const atFloor = computed(() => floor.value !== null && draft.value <= floor.value);

function stepDraft(direction) {
  // Nothing to step by. The controls are away too, so this is the backstop.
  if (!stepsAvailable.value) return;
  // Stepping disarms a pending confirm, so a second tap can never land on a
  // target that moved while the thumb was travelling.
  disarmCancel();
  draftTouched.value = true;
  const step = baseIncrement.value;
  if (direction < 0) {
    draft.value = floor.value === null
      ? Math.max(step, draft.value - step)
      : Math.max(floor.value, draft.value - step);
    return;
  }
  draft.value += step;
}

// Re-seed on a new asset: a draft carried across assets is a ceiling set
// against the wrong price.
watch(() => props.assetId, () => {
  draft.value = props.suggestedProxy;
  draftTouched.value = false;
  closeField();
  disarmCancel();
});

// A seed that moves under an UNTOUCHED tray is followed: the app re-floors it
// when a proxy is spent, and the tray showing the old number would be showing a
// ceiling that has already lost. A seed that moves under a tray the dealer has
// stepped is ignored — their number outranks the suggestion.
watch(() => props.suggestedProxy, (next) => {
  if (!draftTouched.value) draft.value = next;
});

// ── The primary: one control, three readings ───────────────────────────────
// The way out of a proxy is to leave the figure alone and press again, and
// stepping away turns it back into an update.
const draftUntouched = computed(() => props.proxyAmount !== null && !draftTouched.value);

const primaryReading = computed(() => {
  if (props.proxyAmount === null) return 'set';
  return draftUntouched.value ? 'cancel' : 'update';
});

const primaryLabel = computed(() => {
  // While the field is open the primary accepts the TYPED NUMBER and nothing
  // else. One extra zero is a ten-times ceiling, so committing straight from a
  // field the dealer is still in is the mistake the two-step exists to prevent.
  if (typing.value) return text.value.useAmount;
  if (cancelArmed.value) return text.value.cancelProxyConfirm;
  if (primaryReading.value === 'set') return text.value.setProxy;
  return primaryReading.value === 'cancel' ? text.value.cancelProxy : text.value.updateProxy;
});

// The confirm wording is the whole control, so it carries no figure beside it.
const primaryAmount = computed(() => {
  if (cancelArmed.value) return null;
  if (typing.value) return draft.value;
  return primaryReading.value === 'cancel' ? props.proxyAmount : draft.value;
});

const primaryTone = computed(() => {
  // The commit tone survives typing even where the control would otherwise
  // rest on cancel: what it does while the field is open is accept a number,
  // and nothing destructive is one tap away.
  if (typing.value) return 'commit';
  if (cancelArmed.value) return 'confirming';
  return primaryReading.value === 'cancel' ? 'danger' : 'commit';
});

function pressPrimary() {
  // The draft is already written — `onFieldInput` keeps it in step as the
  // dealer types — so accepting is closing the field. The ceiling is still
  // submitted from the resting control, which is the second of the two steps.
  if (typing.value) {
    closeField();
    return;
  }
  if (primaryReading.value === 'cancel') {
    clearProxy();
    return;
  }
  setProxy();
}

// Clamped at SUBMIT and not only at seed time — the price can climb while the
// tray sits open, and the failure when it does is silence.
function setProxy() {
  const amount = floor.value === null ? draft.value : Math.max(draft.value, floor.value);
  emit('set-proxy', props.assetId, amount);
  open.value = false;
}

// ── Canceling a proxy ──────────────────────────────────────────────────────
// Destructive and not undoable from here — re-arming means re-entering a
// ceiling the price may already have passed — so it takes two taps. Leaving the
// tab, stepping the draft, or unmounting disarms, so a stray tap never sits
// waiting.
const CANCEL_CONFIRM_MS = 5000;
const cancelArmed = ref(false);
let cancelTimer = null;

function disarmCancel() {
  if (cancelTimer !== null) { clearTimeout(cancelTimer); cancelTimer = null; }
  cancelArmed.value = false;
}

function clearProxy() {
  if (!cancelArmed.value) {
    cancelArmed.value = true;
    cancelTimer = setTimeout(() => { cancelArmed.value = false; }, CANCEL_CONFIRM_MS);
    return;
  }
  disarmCancel();
  emit('clear-proxy', props.assetId);
  open.value = false;
}

watch(mode, () => { disarmCancel(); closeField(); });

onUnmounted(() => {
  disarmCancel();
  if (ticker !== null) clearInterval(ticker);
  ticker = null;
});

// ── The numeric field ──────────────────────────────────────────────────────
// The OS numeric keyboard on a real input, taking the chips' slot rather than
// adding a keypad below them — which is what keeps the panel one height and
// gets paste, autofill, dictation and hardware keyboards for free.
async function openField() {
  // Seeded from the current draft, never from zero: a dealer who nudges to
  // $26,500 and then taps Type expects to edit that number.
  fieldText.value = String(draft.value);
  typing.value = true;
  disarmCancel();
  await nextTick();
  fieldRef.value?.focus();
}

function closeField() {
  typing.value = false;
}

// Digits only. A pasted `$28,600` is the case worth keeping — it is what a
// dealer copies out of a worksheet — and a decimal is not a bid.
function onFieldInput(event) {
  const digits = event.target.value.replace(/\D/g, '');
  fieldText.value = digits;
  event.target.value = digits;
  if (digits !== '') {
    draft.value = Number(digits);
    draftTouched.value = true;
  }
}

const belowFloor = computed(
  () => typing.value && floor.value !== null && fieldText.value !== '' && Number(fieldText.value) < floor.value
);

const belowFloorLabel = computed(() =>
  interpolate(text.value.belowFloor, { amount: money(floor.value ?? 0) })
);

// ── Bidding as ─────────────────────────────────────────────────────────────
// DealershipPicker marks the chosen chip (and falls back to the first entry
// when the model is absent). The tray only formats each row's meta line.
// The account alone when the dealer stands at that dealership in no stated
// capacity — `role` is optional on the record, and interpolating an absent one
// renders the word `null` beside the account number.
const metaFor = (dealership) => (
  dealership.role
    ? interpolate(text.value.dealershipMeta, { role: dealership.role, account: dealership.id })
    : `#${dealership.id}`
);

// The first line is the dealership's name. A record without one carries its
// badge as its name, and that is a number, not a name — so it reads as the
// translation's word for a bidder, with the badge kept on the second line.
const nameFor = (dealership) => {
  const name = typeof dealership.name === 'string' ? dealership.name.trim() : '';
  return name && name !== dealership.id ? name : text.value.unnamedDealership;
};

const dealershipPickerItems = computed(() => props.biddingAsDealerships.map((dealership) => ({
  id: dealership.id,
  name: nameFor(dealership),
  meta: metaFor(dealership),
})));



// ── The receipt ────────────────────────────────────────────────────────────
const lockedText = computed(() => props.disabled?.text || text.value.bidUnavailable);
const lockedIcon = computed(() => props.disabled?.icon || null);

// ── Placing a bid ──────────────────────────────────────────────────────────
// Emits and dismisses. The rival bids and the win play out on the live block
// behind the tray.
function placeBid() {
  emit('place-bid', props.assetId, liveAmount.value);
  open.value = false;
}

// ── Contradictions the tray cannot catch for itself ────────────────────────
// BidCard's mechanism verbatim: one `console.warn` per complaint, in
// development only. Four of these are the card's, unchanged. The fifth is the
// tray's own, and it is the only one that also needs a RENDERED answer — a
// development warning does nothing for a dealer, so the roster falls back to
// its first entry above and draws something true.
if (import.meta.env?.DEV) {
  reportContradictions();
}

function reportContradictions() {
  const complaints = [];
  if (props.final && props.canBid) complaints.push('`final` with `canBid` — a settled asset cannot take a bid');
  if (props.disabled && props.canBid) complaints.push('`disabled` with `canBid` — rendering locked, which outranks it');
  if (props.proxySpent && props.proxyAmount === null) complaints.push('`proxySpent` with no `proxyAmount` — the amount has to survive being spent');
  if (props.winning && props.final) complaints.push('`winning` with `final` — a settled asset has an outcome, not a live standing');
  // Only worth saying when there IS a roster: an empty one draws no chip row,
  // so a `biddingAsId` that matches nothing in it is the honest state rather
  // than a contradiction, and warning about it would fire on every tray opened
  // before the runtime token resolves.
  if (
    props.biddingAsDealerships.length
    && !props.biddingAsDealerships.some((entry) => entry.id === biddingAsId.value)
  ) {
    complaints.push('`biddingAsId` absent from `biddingAsDealerships` — the marked chip is how the dealer reads which dealership is bidding');
  }
  for (const complaint of complaints) {
    // eslint-disable-next-line no-console
    console.warn(`[BidSheet] asset ${props.assetId}: ${complaint}`);
  }
}
</script>

<style scoped>
.bid-sheet__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── The asset ─────────────────────────────────────────────────────────── */
.bid-sheet__asset {
  display: flex;
  gap: var(--p-bid-sheet-asset-gap);
  align-items: center;
  margin-bottom: var(--p-bid-sheet-asset-margin-bottom);
}

.bid-sheet__thumbnail {
  position: relative;
  width: var(--p-bid-sheet-thumbnail-width);
  height: var(--p-bid-sheet-thumbnail-height);
  border-radius: var(--p-bid-sheet-thumbnail-border-radius);
  flex-shrink: 0;
  overflow: hidden;
  background: var(--p-bid-sheet-thumbnail-background);
}


.bid-sheet__headline {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  /* 14px is the auction lights' size; height is structure, not a theme. */
  height: 14px;
  gap: var(--p-bid-sheet-headline-gap);
  padding: var(--p-bid-sheet-headline-padding);
  border-radius: var(--p-bid-sheet-headline-border-radius);
  background: var(--p-bid-sheet-headline-background);
  color: var(--p-bid-sheet-headline-color);
  font-size: var(--p-bid-sheet-headline-font-size);
  font-weight: var(--p-bid-sheet-headline-font-weight);
  line-height: 1;
  white-space: nowrap;
}

.bid-sheet__headline::before {
  content: '•';
  font-size: 10px;
  line-height: 1;
  color: var(--p-bid-sheet-headline-color);
  flex-shrink: 0;
}

.bid-sheet__identity { flex: 1; min-width: 0; }

.bid-sheet__title {
  font-size: var(--p-bid-sheet-title-font-size);
  font-weight: var(--p-bid-sheet-title-font-weight);
  letter-spacing: var(--p-bid-sheet-title-letter-spacing);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bid-sheet__run {
  display: flex;
  align-items: center;
  gap: var(--p-bid-sheet-run-gap);
  margin-top: var(--p-bid-sheet-run-margin-top);
  font-family: var(--p-bid-sheet-run-font-family);
  font-size: var(--p-bid-sheet-run-font-size);
  color: var(--p-bid-sheet-run-color);
}

/* Weight as well as color, so the last ten seconds survive greyscale. */
.bid-sheet__clock--urgent {
  color: var(--p-bid-sheet-clock-urgent-color);
  font-weight: var(--p-bid-sheet-clock-urgent-font-weight);
}

.bid-sheet__marks {
  display: flex;
  align-items: center;
  gap: var(--p-bid-sheet-marks-gap);
  margin-top: var(--p-bid-sheet-marks-margin-top);
}

.bid-sheet__auction-lights { display: flex; gap: var(--p-bid-sheet-auction-lights-gap); }

/* `light` alone is one of PrimeVue's color-scheme words, so the modifier is
   spelled out — the same reason BidCard writes `auctionLight`. */
.bid-sheet__auction-light {
  width: var(--p-bid-sheet-auction-light-size);
  height: var(--p-bid-sheet-auction-light-size);
  border-radius: var(--p-bid-sheet-auction-light-border-radius);
  display: grid;
  place-items: center;
  font-size: var(--p-bid-sheet-auction-light-font-size);
  font-weight: var(--p-bid-sheet-auction-light-font-weight);
}

/* `auctionLight`, never `light`: `light` is one of PrimeVue's color-scheme
   branch keys and would be dropped from the emitted token name. The hues are
   the auction house's own codes and reach the theme as brand primitives, and
   the ink follows each fill's luminance rather than being fixed — which is what
   keeps every pip above AA. Five here where BidCard has four: white is a code
   the feed sends on nearly every asset, and the tray paints it. */
.bid-sheet__auction-light--red {
  background: var(--p-bid-sheet-auction-light-red-background);
  color: var(--p-bid-sheet-auction-light-red-color);
}
.bid-sheet__auction-light--green {
  background: var(--p-bid-sheet-auction-light-green-background);
  color: var(--p-bid-sheet-auction-light-green-color);
}
.bid-sheet__auction-light--yellow {
  background: var(--p-bid-sheet-auction-light-yellow-background);
  color: var(--p-bid-sheet-auction-light-yellow-color);
}
.bid-sheet__auction-light--blue {
  background: var(--p-bid-sheet-auction-light-blue-background);
  color: var(--p-bid-sheet-auction-light-blue-color);
}
.bid-sheet__auction-light--white {
  background: var(--p-bid-sheet-auction-light-white-background);
  color: var(--p-bid-sheet-auction-light-white-color);
}

.bid-sheet__mark {
  padding: var(--p-bid-sheet-mark-padding);
  border-radius: var(--p-bid-sheet-mark-border-radius);
  background: var(--p-bid-sheet-mark-background);
  color: var(--p-bid-sheet-mark-color);
  font-size: var(--p-bid-sheet-mark-font-size);
  font-weight: var(--p-bid-sheet-mark-font-weight);
  letter-spacing: var(--p-bid-sheet-mark-letter-spacing);
}

.bid-sheet__mark--liked {
  background: var(--p-bid-sheet-mark-liked-background);
  color: var(--p-bid-sheet-mark-liked-color);
}

/* ── The money ─────────────────────────────────────────────────────────── */
.bid-sheet__money {
  display: flex;
  align-items: baseline;
  gap: var(--p-bid-sheet-money-gap);
  margin-bottom: var(--p-bid-sheet-money-margin-bottom);
}

.bid-sheet__price {
  font-family: var(--p-bid-sheet-price-font-family);
  font-size: var(--p-bid-sheet-price-font-size);
  font-weight: var(--p-bid-sheet-price-font-weight);
  color: var(--p-bid-sheet-price-color);
}

.bid-sheet__price--unset {
  font-size: var(--p-bid-sheet-price-unset-font-size);
  color: var(--p-bid-sheet-price-unset-color);
}

.bid-sheet__money--warn .bid-sheet__price { color: var(--p-bid-sheet-price-warn-color); }
.bid-sheet__money--danger .bid-sheet__price { color: var(--p-bid-sheet-price-danger-color); }

/* Mute final trays: the price figure uses muted regardless of severity. Winning
   (not final, sale still live) keeps warning on the figure. */
.bid-sheet__money--final .bid-sheet__price { color: var(--p-bid-sheet-price-final-color); }

.bid-sheet__money-label {
  font-size: var(--p-bid-sheet-money-label-font-size);
  color: var(--p-bid-sheet-money-label-color);
}

.bid-sheet__target {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--p-bid-sheet-target-gap);
  font-size: var(--p-bid-sheet-target-font-size);
  color: var(--p-bid-sheet-target-color);
}

/* ── The receipt ───────────────────────────────────────────────────────── */
.bid-sheet__receipt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-bid-sheet-receipt-gap);
  margin: 0;
  min-height: var(--p-bid-sheet-panel-height);
  border-radius: var(--p-bid-sheet-receipt-border-radius);
  background: var(--p-bid-sheet-receipt-background);
  color: var(--p-bid-sheet-receipt-color);
  font-size: var(--p-bid-sheet-receipt-font-size);
  font-weight: var(--p-bid-sheet-receipt-font-weight);
}

.bid-sheet__receipt--warn {
  background: var(--p-bid-sheet-receipt-warn-background);
  color: var(--p-bid-sheet-receipt-warn-color);
}

.bid-sheet__receipt--danger {
  background: var(--p-bid-sheet-receipt-danger-background);
  color: var(--p-bid-sheet-receipt-danger-color);
}

/* Final receipt: muted text, 1px line border, never warn/danger mix. The
   receipt sentence still names Won / Sold; the muted tone is reference. */
.bid-sheet__receipt--final {
  background: var(--p-bid-sheet-receipt-background);
  color: var(--p-bid-sheet-receipt-final-color);
  border: 1px solid var(--p-bid-sheet-receipt-final-border-color);
}

/* ── The tabs ──────────────────────────────────────────────────────────── */
.bid-sheet__tabs {
  display: flex;
  gap: var(--p-bid-sheet-tabs-gap);
  padding: var(--p-bid-sheet-tabs-padding);
  background: var(--p-bid-sheet-tabs-background);
  border-radius: var(--p-bid-sheet-tabs-border-radius);
  margin-bottom: var(--p-bid-sheet-tabs-margin-bottom);
}

.bid-sheet__tab {
  flex: 1;
  height: var(--p-bid-sheet-tab-height);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-bid-sheet-tab-gap);
  border-radius: var(--p-bid-sheet-tab-border-radius);
  cursor: pointer;
  font-family: inherit;
  font-weight: var(--p-bid-sheet-tab-font-weight);
  font-size: var(--p-bid-sheet-tab-font-size);
  border: none;
  background: transparent;
  color: var(--p-bid-sheet-tab-color);
  transition: background 160ms, color 160ms;
}

.bid-sheet__tab--selected {
  background: var(--p-bid-sheet-tab-selected-background);
  color: var(--p-bid-sheet-tab-selected-color);
}

.bid-sheet__tab:disabled { opacity: var(--p-bid-sheet-tab-disabled-opacity); cursor: not-allowed; }

.bid-sheet__tab-lock { font-size: var(--p-bid-sheet-tab-lock-font-size); }

/* ── The panel ─────────────────────────────────────────────────────────── */
/* Increment, chips and control keep their own heights, so switching tabs
   and opening the field cannot move the slide. The dealer row is optional
   and sits between the chips and the control; a fixed box around all of
   them would crush it or leave a hole. The receipt still matches the
   increment-plus-control stack via --p-bid-sheet-panel-height (160px: the
   16px label, 48px chips, 64px control, and two 16px gaps). */
.bid-sheet__panel {
  display: flex;
  flex-direction: column;
  gap: var(--p-bid-sheet-panel-gap);
}

.bid-sheet__increment-label {
  height: var(--p-bid-sheet-increment-label-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--p-bid-sheet-increment-label-font-size);
  color: var(--p-bid-sheet-increment-label-color);
  letter-spacing: var(--p-bid-sheet-increment-label-letter-spacing);
  text-transform: uppercase;
}

.bid-sheet__increment-hint { text-transform: none; letter-spacing: 0; }

.bid-sheet__increment-hint--changed {
  color: var(--p-bid-sheet-increment-label-changed-color);
  font-weight: var(--p-bid-sheet-increment-label-changed-font-weight);
}

.bid-sheet__type-toggle {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  text-transform: none;
  letter-spacing: 0;
  color: var(--p-bid-sheet-type-toggle-color);
  font-weight: var(--p-bid-sheet-type-toggle-font-weight);
}

.bid-sheet__chips,
.bid-sheet__field {
  display: flex;
  gap: var(--p-bid-sheet-chips-gap);
  height: var(--p-bid-sheet-chips-height);
}

.bid-sheet__chip {
  flex: 1;
  border-radius: var(--p-bid-sheet-chip-border-radius);
  cursor: pointer;
  font-family: var(--p-bid-sheet-chip-font-family);
  background: var(--p-bid-sheet-chip-background);
  color: var(--p-bid-sheet-chip-color);
  border: 1px solid var(--p-bid-sheet-chip-border-color);
  font-weight: var(--p-bid-sheet-chip-font-weight);
  font-size: var(--p-bid-sheet-chip-font-size);
}

/* The dash that takes the ladder's place. A cell, not a control: the chips'
   own box and height, with the muted ink the increment label uses. */
.bid-sheet__chip--unset {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-bid-sheet-increment-label-color);
}

/* Before `--selected`, so the chosen multiple still reads as chosen while the
   ladder is marked. The mark is a border and a weight — nothing that changes a
   chip's box. */
.bid-sheet__chip--changed {
  border-color: var(--p-bid-sheet-chip-changed-border-color);
}

.bid-sheet__chip--selected {
  background: var(--p-bid-sheet-chip-selected-background);
  color: var(--p-bid-sheet-chip-selected-color);
  border-color: var(--p-bid-sheet-chip-selected-border-color);
}

.bid-sheet__field { position: relative; }

.bid-sheet__input {
  flex: 1;
  min-width: 0;
  border-radius: var(--p-bid-sheet-input-border-radius);
  border: 1px solid var(--p-bid-sheet-input-border-color);
  background: var(--p-bid-sheet-input-background);
  color: var(--p-bid-sheet-input-color);
  font-family: var(--p-bid-sheet-input-font-family);
  font-size: var(--p-bid-sheet-input-font-size);
  font-weight: var(--p-bid-sheet-input-font-weight);
  text-align: center;
}

.bid-sheet__input--below {
  border-color: var(--p-bid-sheet-input-below-border-color);
  color: var(--p-bid-sheet-input-below-color);
}

.bid-sheet__below {
  position: absolute;
  right: var(--p-bid-sheet-below-inset);
  bottom: var(--p-bid-sheet-below-inset);
  font-size: var(--p-bid-sheet-below-font-size);
  font-weight: var(--p-bid-sheet-below-font-weight);
  color: var(--p-bid-sheet-input-below-color);
}

/* ── The controls ──────────────────────────────────────────────────────── */
.bid-sheet__control { position: relative; height: var(--p-bid-sheet-control-height); }

.bid-sheet__bid {
  position: absolute;
  inset: 0;
  border-radius: var(--p-bid-sheet-bid-border-radius);
  background: var(--p-bid-sheet-bid-background);
  color: var(--p-bid-sheet-bid-color);
  border: none;
  font-family: inherit;
  font-weight: var(--p-bid-sheet-bid-font-weight);
  font-size: var(--p-bid-sheet-bid-font-size);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-bid-sheet-bid-gap);
  cursor: pointer;
  transition: opacity 180ms;
}

.bid-sheet__bid--hidden { opacity: 0; pointer-events: none; }

.bid-sheet__unavailable {
  margin: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--p-bid-sheet-unavailable-gap);
  border-radius: var(--p-bid-sheet-unavailable-border-radius);
  background: var(--p-bid-sheet-unavailable-background);
  color: var(--p-bid-sheet-unavailable-color);
  font-size: var(--p-bid-sheet-unavailable-font-size);
  font-weight: var(--p-bid-sheet-unavailable-font-weight);
  text-align: center;
}

.bid-sheet__hint {
  font-size: var(--p-bid-sheet-hint-font-size);
  font-weight: var(--p-bid-sheet-hint-font-weight);
  color: var(--p-bid-sheet-hint-color);
}

.bid-sheet__steppers { display: flex; gap: var(--p-bid-sheet-steppers-gap); }

.bid-sheet__step {
  width: var(--p-bid-sheet-step-width);
  border-radius: var(--p-bid-sheet-step-border-radius);
  background: var(--p-bid-sheet-step-background);
  color: var(--p-bid-sheet-step-color);
  border: 1px solid var(--p-bid-sheet-step-border-color);
  font-size: var(--p-bid-sheet-step-font-size);
  font-weight: var(--p-bid-sheet-step-font-weight);
  cursor: pointer;
  flex-shrink: 0;
}

.bid-sheet__step:disabled { opacity: var(--p-bid-sheet-step-disabled-opacity); cursor: not-allowed; }

.bid-sheet__primary {
  flex: 1;
  min-width: 0;
  border-radius: var(--p-bid-sheet-primary-border-radius);
  border: none;
  font-family: inherit;
  font-weight: var(--p-bid-sheet-primary-font-weight);
  font-size: var(--p-bid-sheet-primary-font-size);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--p-bid-sheet-primary-gap);
  transition: background 160ms, color 160ms;
}

.bid-sheet__primary--commit {
  background: var(--p-bid-sheet-primary-commit-background);
  color: var(--p-bid-sheet-primary-commit-color);
}

/* Resting on cancel is a quiet tertiary action; the destructive intent becomes
   explicit only once it is one tap away. */
.bid-sheet__primary--danger {
  background: var(--p-bid-sheet-primary-danger-background);
  color: var(--p-bid-sheet-primary-danger-color);
}

.bid-sheet__primary--confirming {
  background: var(--p-bid-sheet-primary-confirming-background);
  color: var(--p-bid-sheet-primary-confirming-color);
}

/* ── Bidding as ────────────────────────────────────────────────────────── */
/* DealershipPicker draws the strip; the tray only keeps flex-shrink. */
.bid-sheet__dealerships {
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .bid-sheet__tab,
  .bid-sheet__bid,
  .bid-sheet__primary { transition: none; }
}
</style>
